<template>
  <main class="page">
    <section class="hero" aria-labelledby="hero-title">
      <div class="hero-content">
        <p class="eyebrow">Eastern Province RASSPL Franchise</p>
        <h1 id="hero-title" class="hero-title">EP Raggies</h1>
        <p class="hero-copy">
          EP Raggies is the Eastern Province franchise of RASSPL Africa, running local rock and
          surf competitions built around 100% catch, photograph and release, clear scoring, and
          fish care first.
        </p>

        <div class="hero-actions">
          <RouterLink class="button primary" to="/competitions">2026-2027 comps</RouterLink>
          <RouterLink class="button primary" to="/rules">View the rules</RouterLink>
          <RouterLink class="button secondary" to="/contact">Get in touch</RouterLink>
        </div>

        <div class="stats-row" aria-label="EP Raggies highlights">
          <div class="stat">
            <strong>1 Jun</strong>
            <span>Competition year begins</span>
          </div>
          <div class="stat">
            <strong>100%</strong>
            <span>Catch, photograph and release</span>
          </div>
          <div class="stat">
            <strong>8</strong>
            <span>EP Raggies league events per season</span>
          </div>
          <div class="stat">
            <strong>12h</strong>
            <span>Maximum fishing time per competition</span>
          </div>
        </div>
      </div>
    </section>

    <section class="sponsor-marquee-band" aria-label="EP Raggies sponsors">
      <RouterLink class="sponsor-marquee-link" to="/sponsors">
        <span class="sponsor-marquee-label">Proudly supported by</span>
        <span class="sponsor-marquee-window sponsor-marquee-window-desktop" aria-hidden="true">
          <span class="sponsor-marquee-track">
            <span
              v-for="(sponsor, index) in sponsorMarqueeItems"
              :key="`${sponsor.file}-${index}`"
              class="sponsor-marquee-item"
            >
              <img :src="sponsor.src" :alt="sponsor.name" loading="lazy" />
            </span>
          </span>
        </span>
        <span class="sponsor-marquee-window sponsor-marquee-window-mobile">
          <span :key="activeSponsor.file" class="sponsor-featured-logo">
            <img
              :src="activeSponsor.src"
              :alt="`${activeSponsor.name} sponsor logo`"
              loading="lazy"
            />
          </span>
          <span :key="`${activeSponsor.file}-name`" class="sponsor-featured-name">
            {{ activeSponsor.name }}
          </span>
        </span>
      </RouterLink>
    </section>

    <section class="section retained-docs-band" aria-labelledby="doc-mini-title">
      <div class="section-inner retained-docs-panel">
        <div>
          <p class="eyebrow">Document your catch</p>
          <h2 id="doc-mini-title" class="section-title">Three clean photos. One fish safely released.</h2>
          <p class="section-lead">
            Keep the fish wet, make the scorecard visible, measure clearly, and release before
            casting again.
          </p>
        </div>

        <div class="retained-docs-steps" aria-label="Fish documentation quick steps">
          <article
            v-for="step in documentSteps"
            :key="step.title"
            class="retained-doc-step"
          >
            <img :src="step.icon" :alt="step.alt" loading="lazy" />
            <div>
              <strong>{{ step.title }}</strong>
              <span>{{ step.copy }}</span>
            </div>
          </article>

          <RouterLink class="doc-help-link" to="/document-fish">Need more help?</RouterLink>
        </div>
      </div>
    </section>

    <section class="section leaderboard-band" aria-labelledby="leaderboard-title">
      <div class="section-inner">
        <p class="eyebrow">After Comp 3</p>
        <h2 id="leaderboard-title" class="section-title">Current leaderboard</h2>
        <p class="section-lead">
          The latest standings are in. Here is the Comp 3 leaderboard for everyone to see.
        </p>

        <div class="leaderboard-frame">
          <img
            src="/Comp3Leaderboard.jpeg"
            alt="Comp 3 leaderboard standings"
          />
        </div>
      </div>
    </section>

    <section class="section" aria-labelledby="competition-title">
      <div class="section-inner split-section">
        <div>
          <p class="eyebrow">The franchise</p>
          <h2 id="competition-title" class="section-title">Eastern Province shore angling with conservation at the core.</h2>
          <p class="section-lead">
            EP Raggies follows the RASSPL Africa scorecard process: every catch is witnessed by
            another competitor or official, photographed with the day symbol where required,
            measured on a clean wet mat, and released alive before the angler casts again.
          </p>
          <div class="page-actions">
            <RouterLink class="button primary" to="/about">About EP Raggies</RouterLink>
            <RouterLink class="button primary" to="/signup">Member signup</RouterLink>
            <RouterLink class="button secondary" to="/login">Member login</RouterLink>
          </div>
        </div>

        <div class="media-panel" aria-hidden="true">
          <img src="@/assets/img/icons/RassplFish.png" alt="" />
        </div>
      </div>
    </section>

    <section class="section season-band" aria-labelledby="season-title">
      <div class="section-inner">
        <p class="eyebrow">Latest competition</p>
        <h2 id="season-title" class="section-title">Competition venues across the coast.</h2>
        <p class="section-lead">
          The Eastern Province season runs from Pipe to Pattie Parking Lot through to Sundays Surf
          and Blue Horizon Bay, with weather, map links and tide windows available on the comps page.
        </p>

        <div class="retained-season-grid" aria-label="Competition venues">
          <article
            v-for="comp in seasonComps"
            :key="comp.number"
            class="retained-season-card"
          >
            <RouterLink
              :to="`/competitions#comp-${comp.number}`"
              :aria-label="`View Comp ${comp.number}: ${comp.venue}`"
            >
              <img v-if="comp.image" :src="comp.image" :alt="`${comp.venue} competition poster`" loading="lazy" />
              <span>Comp {{ comp.number }}</span>
              <strong>{{ comp.venue }}</strong>
              <small>{{ comp.date }}</small>
            </RouterLink>
          </article>
        </div>

        <div class="page-actions">
          <RouterLink class="button primary" to="/competitions">Open competition schedule</RouterLink>
        </div>
      </div>
    </section>

    <section class="section callout-band" aria-labelledby="values-title">
      <div class="section-inner">
        <p class="eyebrow">Why anglers join</p>
        <h2 id="values-title" class="section-title">A league for serious competition, new anglers, and better fish handling.</h2>

        <div class="feature-grid">
          <article class="feature-card">
            <img src="@/assets/img/icons/RassplFish.png" alt="" />
            <h3>Fish care comes first</h3>
            <p>Buckets stay filled with seawater, fish stay wet, and air exposure is kept as short as possible.</p>
          </article>
          <article class="feature-card">
            <img src="@/assets/img/icons/RassplLogo1.png" alt="" />
            <h3>Eastern Province franchise events</h3>
            <p>EP Raggies runs up to eight league competitions inside its designated coastal boundaries each season.</p>
          </article>
          <article class="feature-card">
            <img src="@/assets/img/icons/RassplText.png" alt="" />
            <h3>Transparent points</h3>
            <p>Species points, weight points, bonus points and attendance points all feed into competition standings.</p>
          </article>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { loadPublicCompetitions } from '../data/publicCompetitions';
import { fallbackSponsors, loadPublicSponsors } from '../data/sponsors';

const documentSteps = [
  {
    title: 'Bucket photo',
    icon: '/FishDocumentSteps/FishInBucketIcon.png',
    alt: 'Bucket photo icon',
    copy: 'Fish in seawater with the day code visible where needed.',
  },
  {
    title: 'Mat photo',
    icon: '/FishDocumentSteps/FishOnMatIcon.png',
    alt: 'Mat photo icon',
    copy: 'Nose on zero, whole fish visible, scorecard in frame.',
  },
  {
    title: 'Angler photo',
    icon: '/FishDocumentSteps/HoldingFishIcon.png',
    alt: 'Angler holding fish icon',
    copy: 'For prize-category fish, pose safely over the bucket.',
  },
];

export default {
  name: 'HomeView',
  data() {
    return {
      documentSteps,
      seasonComps: [],
      sponsors: fallbackSponsors,
      sponsorMarqueeItems: [...fallbackSponsors, ...fallbackSponsors],
      sponsorIndex: 0,
      sponsorIntervalId: null,
    };
  },
  computed: {
    activeSponsor() {
      return this.sponsors[this.sponsorIndex] || this.sponsors[0];
    },
  },
  mounted() {
    loadPublicCompetitions().then(data => { this.seasonComps = data.days.map(day => ({ ...day, date: day.displayDate })); }).catch(() => {});
    this.loadSponsors();
    this.sponsorIntervalId = window.setInterval(() => {
      this.sponsorIndex = (this.sponsorIndex + 1) % this.sponsors.length;
    }, 2600);
  },
  beforeUnmount() {
    window.clearInterval(this.sponsorIntervalId);
  },
  methods: {
    async loadSponsors() {
      try {
        const sponsors = await loadPublicSponsors();
        if (sponsors.length) {
          this.sponsors = sponsors;
          this.sponsorMarqueeItems = [...sponsors, ...sponsors];
          this.sponsorIndex = 0;
        }
      } catch (error) {
        this.sponsors = fallbackSponsors;
      }
    },
  },
};
</script>
